import {
  ComponentRef,
  ElementRef,
  Injectable,
  ViewContainerRef,
} from "@angular/core";
import L from "./leaflet-plugins-loader";
import { SidePanelOptions } from "leaflet";
import { TranslateService } from "@ngx-translate/core";
import { ILeafletInstance, ITrackingRoute } from "./leaflet.interface";
import { RouteTracking } from "./route-tracking";
import { WidgetSettingsObserveService } from "../../../shared/services/helpers/widget-settings-observe.service";
import {
  IMapWidgetFormGroupEntities,
  IMapWidgetFormGroupSettings,
} from "../../map-widget-settings/map-widget-settings.interface";
import { INgsiMapBaseEntity } from "../ngsild/interfaces/ngsi-entity.interface";
import { IAppPluginWidgetSettings } from "core-client/api";
import { INgsiMaritimeTargets } from "../ngsild/interfaces/ngsi-maritime-targets.interface";
import * as NgsildConstants from "../ngsild/helpers/ngsild.constants";
import * as NgsiAIS from "../ngsild/models/ngsi-ais";
import { CommonMapHelperService } from "./helper/common-map-helper.service";
import {
  IMapToolbarSettingDisplayChanges,
  MapToolbarSettingsComponent,
} from "../../map-widget-view/map-toolbar-settings/map-toolbar-settings.component";
import * as leafletInstanceConfig from "../ngsild/helpers/leaflet-instance-config.constants";
import { IMapWidgetPermissions } from "../../../shared/interfaces/map-widget-permission.interface";
import { LeafletInstanceConfigConstants } from "../ngsild/helpers/leaflet-instance-config.constants";

@Injectable({
  providedIn: "root",
})
export class LeafletService {
  // https://leafletjs.com/reference.html#map-pane
  private static readonly MAP_ID_PREFIX: string = "map_";
  mapInstances: Map<string, ILeafletInstance> = new Map();

  private readonly CLEAR_TIMEOUT: number = 30000; // 30 seconds in milliseconds / clearing the temporary layers
  private readonly CLEAR_TIMEOUT_AIS: number = 240000; // 240 seconds in milliseconds / clearing the AIS temporary layers
  private readonly CLEAR_TIMEOUT_FusionMaritimeTarget: number = 600000; // 10 minutes / clearing the FusionMaritimeTarget temporary layers

  private readonly latitude!: number;
  private readonly longitude!: number;
  private zoom: number;
  private currentLocation!: L.Control.Locate;
  centerOnMarkerActive: boolean;

  constructor(
    public translator: TranslateService,
    protected widgetSettingObserve: WidgetSettingsObserveService,
  ) {
    this.latitude = widgetSettingObserve.defaultWidgetSettings.latitude;
    this.longitude = widgetSettingObserve.defaultWidgetSettings.longitude;
    this.zoom = widgetSettingObserve.defaultWidgetSettings.zoom;
    this.centerOnMarkerActive =
      widgetSettingObserve.defaultWidgetSettings.centerMapOnMarkers;
  }

  getInstanceId(id: number | string): string {
    return `${LeafletService.MAP_ID_PREFIX}${id}`;
  }

  getInstance(id: number | string): ILeafletInstance | undefined {
    return this.mapInstances.get(this.getInstanceId(id)) ?? undefined;
  }

  removeInstance(id: number | string): void {
    this.mapInstances.delete(this.getInstanceId(id));
  }

  createInstance(id: number | string): void {
    const instance = this.getInstance(id);

    if (!instance) {
      const leafletMap = new L.Map(this.getInstanceId(id), {
        layers: [],
        zoomControl: false,
        zoomSnap: 0.25,
        contextmenu: true,
        contextmenuItems: [],
      } as any);

      const zoomControl = L.control.zoom({
        position: "bottomright",
        zoomInTitle: this.translator.instant("MAP.ZOOM_IN"),
        zoomOutTitle: this.translator.instant("MAP.ZOOM_OUT"),
      });
      leafletMap.attributionControl.setPrefix("");

      const leafletInstance: ILeafletInstance = {
        id: this.getInstanceId(id),
        map: leafletMap,
        mapReady: false,
        followTargetIconEl: null,
        zoomInButtonEl: null,
        zoomOutButtonEl: null,
        rulerIconButtonEl: null,
        dataConfig: JSON.parse(JSON.stringify(leafletInstanceConfig.config)),
        followedTarget: null,
        zoom: this.zoom,
        latitude: this.latitude,
        longitude: this.longitude,
        centerOnMarkerActive: this.centerOnMarkerActive,
        tileLayers: {},
        entities: new Map(),
        additionalEntities: new Map(),
        layers: new Map(),
        markers: new Map(),
        circles: new Map(),
        polygons: new Map(),
        polylines: new Map(),
        trackingRoutes: new Map(),
        layerControls: new Map(),
        scheduledEntityTimeouts: new Map(),
        timeoutIds: [],
      };

      leafletInstance.map.addControl(zoomControl);
      this.mapInstances.set(this.getInstanceId(id), leafletInstance);
    }
  }

  updateMapDataConfig(
    id: number | string,
    updateMapDataConfig: LeafletInstanceConfigConstants,
  ) {
    const mapInstance = this.mapInstances.get(this.getInstanceId(id));

    if (mapInstance) {
      mapInstance.dataConfig = updateMapDataConfig;
      this.mapInstances.set(this.getInstanceId(id), mapInstance);
    }
  }

  initMap(instance?: ILeafletInstance): void {
    if (instance) {
      instance.map.setView(
        new L.LatLng(instance.latitude, instance.longitude),
        instance.zoom,
      );

      instance.map.whenReady(() => {
        instance.mapReady = true;
      });

      instance.map.invalidateSize();

      this.listenOnMapDragging(instance);
      this.listenOnMouseWheel(instance);
      this.listenOnZoomButtonChanges(instance);
    }
  }

  resizeMap(instance?: ILeafletInstance): void {
    if (instance) {
      instance.map.invalidateSize();
    }
  }

  isVisibleOnMap(
    entity: INgsiMapBaseEntity,
    instance?: ILeafletInstance,
  ): boolean {
    if (!instance) {
      return false;
    }

    const bounds = instance.map.getBounds();

    if (entity.location.type === "LineString") {
      const coordsStart = entity.location.coordinates[0] as unknown as [
        number,
        number,
      ];
      // const coordsEnd = entity.location.coordinates[entity.location.coordinates.length -1] as unknown as [number, number]
      return bounds.contains([
        [coordsStart[1], coordsStart[0]],
        // [coordsEnd[1], coordsEnd[0]]
      ]);
    }

    return bounds.contains([
      entity.location.coordinates[1],
      entity.location.coordinates[0],
    ]);
  }

  noDataFound(identifier: string | null, instance?: ILeafletInstance): void {
    if (instance) {
      instance.map.setView(
        new L.LatLng(instance.latitude, instance.longitude),
        instance.zoom,
      );
      if (identifier === "AIS") {
        const messages = NgsiAIS.getPopupNoData(
          this.translator,
          instance.latitude,
          instance.longitude,
        );
        messages.openOn(instance.map);

        setTimeout(function () {
          messages.remove();
        }, 2000);
      }

      instance.map.invalidateSize();
    }
  }

  activateLayer(layerLabel: string, instance?: ILeafletInstance): void {
    if (instance?.map) {
      const tileLayer = instance.tileLayers[layerLabel];
      if (tileLayer) {
        instance.zoom = tileLayer.zoom;
        this.zoom = tileLayer.zoom;

        if (!instance.map.hasLayer(tileLayer.layer)) {
          tileLayer.layer.addTo(instance.map);
        }
      }
    }
  }

  addLayerControl(id: number | string, instance?: ILeafletInstance): void {
    if (instance) {
      const mapId = this.getInstanceId(id);
      let layerControl = instance.layerControls.get(mapId);
      if (!layerControl) {
        layerControl = L.control.scale();
        instance.layerControls.set(mapId, layerControl);
      }

      layerControl.addTo(instance.map);
    }
  }

  removeLayerControl(id: number | string, instance?: ILeafletInstance): void {
    if (instance) {
      const layerControl = instance.layerControls.get(this.getInstanceId(id));
      if (layerControl) {
        instance.layerControls.forEach((layerControl: L.Control) => {
          // Remove the associated DOM element if it's a custom control (e.g., side panel)
          if (!(layerControl as any)?._panel) {
            layerControl.remove();
          }
        });
        instance.map.removeControl(layerControl);
      }
    }
  }

  removeUnusedTileLayers(layerLabel: string, instance?: ILeafletInstance) {
    if (instance) {
      for (const item in instance.tileLayers) {
        if (item !== layerLabel) {
          const tileLayer = instance.tileLayers[item];

          if (tileLayer) {
            if (instance.map.hasLayer(tileLayer.layer)) {
              instance.map.removeLayer(tileLayer.layer);
            }

            const op = tileLayer.layer.options as any;
            instance.map.attributionControl.removeAttribution(op.attribution);
          }
        }
      }
    }
  }

  removeTileLayer(layerLabel: string, instance?: ILeafletInstance): void {
    if (instance) {
      if (layerLabel in instance.tileLayers) {
        const tileLayer = instance.tileLayers[layerLabel].layer;

        if (instance.map.hasLayer(tileLayer)) {
          instance.map.removeLayer(tileLayer);
        }

        const op = instance.tileLayers[layerLabel].layer.options as any;
        instance.map.attributionControl.removeAttribution(op.attribution);
        instance.map.invalidateSize();
      }
    }
  }

  addMarkerToLayer(
    id: string,
    type: string,
    marker: L.Marker<any>,
    showLayerOnMap: boolean,
    instance?: ILeafletInstance,
  ) {
    if (!instance?.layers) {
      console.warn("Invalid instance or layers");
      return;
    }

    let layerId = `${this.getInstanceId(id)}_${type}`;
    const layer = instance.layers.get(`${layerId}`);

    if (!layer?.hasLayer(marker)) {
      layer?.addLayer(marker);
    }

    if (layer && !instance.map.hasLayer(layer) && showLayerOnMap) {
      layer.addTo(instance.map);
    }
  }

  addPolylineToLayer(
    id: string,
    type: string,
    polyline: L.Polyline<any>,
    instance?: ILeafletInstance,
  ) {
    if (!instance?.layers) {
      console.warn("Invalid instance or layers");
      return;
    }

    let layerId = `${this.getInstanceId(id)}_${type}`;
    const layer = instance.layers.get(`${layerId}`);

    if (!layer?.hasLayer(polyline)) {
      layer?.addLayer(polyline);
    }

    if (layer && !instance.map.hasLayer(layer)) {
      layer.addTo(instance.map);
    }
  }

  addPolygonToLayer(
    id: string,
    type: string,
    polygon: L.Polygon<any>,
    instance?: ILeafletInstance,
  ) {
    if (!instance?.layers) {
      console.warn("Invalid instance or layers");
      return;
    }

    let layerId = `${this.getInstanceId(id)}_${type}`;
    const layer = instance.layers.get(`${layerId}`);

    if (!layer?.hasLayer(polygon)) {
      layer?.addLayer(polygon);
    }

    if (layer && !instance.map.hasLayer(layer)) {
      layer.addTo(instance.map);
    }
  }

  addLayerByType(id: string, type: string, instance?: ILeafletInstance): void {
    if (!instance?.layers) {
      console.warn("Invalid instance or layers");
      return;
    }

    let layerId = `${this.getInstanceId(id)}_${type}`;

    let layer = instance.layers.get(`${layerId}`);

    // Check if the layer already exists
    if (!layer) {
      // Create a new FeatureGroup or Layer based on the type
      layer = L.featureGroup(); // Default to FeatureGroup
      instance.layers.set(layerId, layer);
    }

    // Add the layer to the map if not already added
    if (!instance.map.hasLayer(layer)) {
      layer.addTo(instance.map);
    }
  }

  getLayersByType(
    instance: ILeafletInstance,
    id: string,
  ): Record<string, L.Layer[]> {
    const layersByType: Record<string, L.Layer[]> = {};

    if (!instance?.layers) {
      console.warn("Invalid instance or layers");
      return layersByType;
    }

    instance.layers.forEach((layerGroup: L.FeatureGroup<any>, key: string) => {
      if (key.startsWith(`map_${id}_`)) {
        const type = key.replace(`map_${id}_`, "");
        if (!layersByType[type]) {
          layersByType[type] = [];
        }

        layerGroup.eachLayer((layer: L.Layer) => {
          layersByType[type].push(layer);
        });
      }
    });

    return layersByType;
  }

  getAllLayers(instance: ILeafletInstance): L.Layer[] {
    const allLayers: L.Layer[] = [];

    // Iterate over all layer types in the instance
    instance.layers.forEach((layerGroup) => {
      // Get all layers from the current layer group and add them to the array
      layerGroup.eachLayer((layer) => allLayers.push(layer));
    });

    return allLayers;
  }

  addTemporaryEntities(
    entityId: string,
    entityType: string,
    instance: ILeafletInstance,
  ): void {
    let clearTimer = this.CLEAR_TIMEOUT;

    if (
      entityType ===
      leafletInstanceConfig.config.dataSourceSelector.MMSI.value.type
    ) {
      clearTimer = this.CLEAR_TIMEOUT_AIS;
    }

    if (
      entityType ===
      leafletInstanceConfig.config.dataModel.FusionMaritimeTarget.type
    ) {
      clearTimer = this.CLEAR_TIMEOUT_FusionMaritimeTarget;
    }

    if (instance.scheduledEntityTimeouts.has(entityId)) {
      const oTimeoutId = instance.scheduledEntityTimeouts.get(entityId);
      if (typeof oTimeoutId === "number") {
        clearTimeout(oTimeoutId);
      }
      instance.scheduledEntityTimeouts.set(entityId, null);
    }

    const timeoutId = setTimeout(() => {
      this.removeTemporaryEntity(entityId, instance);
    }, clearTimer);

    instance.scheduledEntityTimeouts.set(entityId, timeoutId);
  }

  excludeFromTemporaryEntities(
    entity: INgsiMaritimeTargets,
    appPluginWidgetSettings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
  ): boolean {
    let exclude: boolean = false;
    if (
      entity.source ===
      leafletInstanceConfig.config.dataModel.MaritimeTargets.dataSources[0][
        "IN_TTD"
      ]
    ) {
      const hasWavelab = appPluginWidgetSettings.entities.some(
        (settingEntity: IMapWidgetFormGroupEntities) =>
          settingEntity.id === NgsildConstants.WAVELAB_ID,
      );
      const waveLabMarker = instance.markers.get(
        `${NgsildConstants.NgsiIdPrefix}${NgsildConstants.WAVELAB_ID}`,
      );
      if (waveLabMarker && hasWavelab) {
        const latlng = waveLabMarker.getLatLng();
        const lat = Number(entity.location.coordinates[1].toFixed(5));
        const lng = Number(entity.location.coordinates[0].toFixed(5));
        exclude =
          lat === Number(latlng.lat.toFixed(5)) &&
          lng === Number(latlng.lng.toFixed(5));

        return exclude;
      }
      return exclude;
    }
    return exclude;
  }

  getTemporaryEntities(
    instance: ILeafletInstance,
  ): Map<string, number | ReturnType<typeof setTimeout> | null> {
    return instance.scheduledEntityTimeouts;
  }

  private removeTemporaryEntity(
    entityId: string,
    instance: ILeafletInstance,
  ): void {
    const marker = instance.markers.get(entityId);

    if (marker) {
      instance.layers.forEach((layer: L.FeatureGroup<any>) => {
        if (layer.hasLayer(marker)) {
          layer.removeLayer(marker);
        }
      });

      instance.markers.delete(entityId);
    }

    if (instance.additionalEntities.has(entityId)) {
      instance.additionalEntities.delete(entityId);
    }

    if (instance.scheduledEntityTimeouts.has(entityId)) {
      instance.scheduledEntityTimeouts.delete(entityId);
    }
  }

  clearTemporaryEntities(
    appPluginWidgetId: string,
    instance: ILeafletInstance,
  ): void {
    instance.scheduledEntityTimeouts.forEach(
      (
        timeoutId: number | ReturnType<typeof setTimeout> | null,
        entityId: string,
      ) => {
        if (typeof timeoutId === "number") {
          clearTimeout(timeoutId);
        }
        this.removeTemporaryEntity(entityId, instance);
      },
    );
  }

  updateLatLng(
    latitude: number,
    longitude: number,
    instance?: ILeafletInstance,
  ): void {
    if (instance) {
      instance.latitude = latitude;
      instance.longitude = longitude;
    }
  }

  updateZoom(mapZoom: number, instance?: ILeafletInstance): void {
    if (instance) {
      instance.zoom = mapZoom;
      instance.map.setZoom(mapZoom);
      instance.map.invalidateSize(true);
    }
  }

  listenOnMapDragging(instance: ILeafletInstance) {
    instance.map.on("dragstart", ($event: L.LeafletEvent) => {
      instance.centerOnMarkerActive = false;

      if (!instance.followTargetIconEl) {
        instance.followTargetIconEl = instance.map
          .getContainer()
          .querySelector("i.followTargetIcon");
      }

      if (instance.followTargetIconEl?.classList.contains("active")) {
        instance.followTargetIconEl.classList.remove("active");
      }
    });
  }

  listenOnMouseWheel(instance: ILeafletInstance) {
    const mapContainer = instance.map.getContainer();

    L.DomEvent.on(mapContainer, "wheel", function () {
      instance.centerOnMarkerActive = false;

      if (!instance.followTargetIconEl) {
        instance.followTargetIconEl =
          mapContainer.querySelector("i.followTargetIcon");
      }

      if (instance.followTargetIconEl?.classList.contains("active")) {
        instance.followTargetIconEl.classList.remove("active");
      }
    });
  }

  listenOnZoomButtonChanges(instance: ILeafletInstance) {
    if (!instance.zoomInButtonEl) {
      instance.zoomInButtonEl = instance.map
        .getContainer()
        .querySelector("a.leaflet-control-zoom-in");
    }
    if (!instance.zoomOutButtonEl) {
      instance.zoomOutButtonEl = instance.map
        .getContainer()
        .querySelector("a.leaflet-control-zoom-out");
    }

    const removeActiveClass = () => {
      instance.centerOnMarkerActive = false;

      if (!instance.followTargetIconEl) {
        instance.followTargetIconEl = instance.map
          .getContainer()
          .querySelector("i.followTargetIcon");
      }

      if (instance.followTargetIconEl?.classList.contains("active")) {
        instance.followTargetIconEl.classList.remove("active");
      }
    };

    if (instance.zoomInButtonEl) {
      L.DomEvent.addListener(
        instance.zoomInButtonEl as any,
        "click",
        removeActiveClass,
        { passive: true },
      );
    }

    if (instance.zoomOutButtonEl) {
      L.DomEvent.addListener(
        instance.zoomOutButtonEl as any,
        "click",
        removeActiveClass,
        { passive: true },
      );
    }
  }

  shouldCenterMapOnMarkers(
    instance: ILeafletInstance,
    settings: IMapWidgetFormGroupSettings,
  ): boolean {
    return settings.centerMapOnMarkers && instance.centerOnMarkerActive;
  }

  centerOnSelectedMarker(
    entity: INgsiMapBaseEntity,
    instance?: ILeafletInstance,
    widgetSettings?: IAppPluginWidgetSettings,
  ) {
    if (!instance) {
      console.warn("Missing leaflet instance to center on selected marker.");
      return;
    }

    if (entity?.id == undefined) {
      const settings = widgetSettings as unknown as IMapWidgetFormGroupSettings;
      if (
        !instance.centerOnMarkerActive &&
        settings?.centerMapOnMarkers === true
      ) {
        instance.centerOnMarkerActive = true;
      }

      return;
    }

    if (instance.centerOnMarkerActive) {
      instance.centerOnMarkerActive = false;
    }

    const marker = instance.markers.get(entity.id);

    if (marker) {
      instance.map.setView(
        [entity.location.coordinates[1], entity.location.coordinates[0]],
        16,
      );
    }
  }

  addSettingsControl(
    id: string,
    viewContainerRef: ViewContainerRef,
    settings: IAppPluginWidgetSettings,
    widgetPermissions: IMapWidgetPermissions,
    toolbarSettingsContainerRef?: ComponentRef<MapToolbarSettingsComponent>,
    instance?: ILeafletInstance,
  ) {
    if (!instance) {
      console.warn("Missing leaflet instance to add custom toolbar.");
      return;
    }

    toolbarSettingsContainerRef = viewContainerRef.createComponent(
      MapToolbarSettingsComponent,
    );
    Object.assign(toolbarSettingsContainerRef.instance, {
      widgetId: id,
      leafletInstance: instance,
      widgetSettings: settings,
      widgetPermissions: widgetPermissions,
    });

    const slideMenuHtml = toolbarSettingsContainerRef.location.nativeElement;

    if (!slideMenuHtml) {
      console.warn("Missing htmlElement to add settings toolbar item.");
      return;
    }

    const slideMenuControl = (L.control as any).slideMenu("", {
      position: "bottomright",
      menuposition: "topright",
      icon: "fa-gear",
      width: "400px",
    });

    instance.layerControls.set(`slideMenuControl_${id}`, slideMenuControl);
    instance.map.addControl(slideMenuControl);

    const closeButton = document.querySelector(
      ".leaflet-menu .leaflet-menu-close-button",
    );
    if (closeButton) {
      closeButton.remove();
    }

    const slideMenuTooltip = document.querySelector(
      ".leaflet-bar-part-single",
    ) as HTMLElement;
    if (slideMenuTooltip) {
      slideMenuTooltip.title = this.translator.instant("MAP.SETTINGS");
    }

    slideMenuControl.setContents("");
    slideMenuControl._contents.appendChild(slideMenuHtml);

    toolbarSettingsContainerRef.instance.toolbarSettingsChanged.subscribe(
      (value: IMapToolbarSettingDisplayChanges) => {
        instance.dataConfig.dataModel.AIS.showTempLayer =
          value.AIS.aISTargetsVisible;
        instance.dataConfig.dataModel.MaritimeTargets.showDefaultLayer =
          value.MaritimeTargets.maritimeTargetsVisible;
        instance.dataConfig.dataModel.RadarTarget.showDefaultLayer =
          value.RadarTarget.radarTargetVisible;
        instance.dataConfig.dataModel.FusionMaritimeTarget.showDefaultLayer =
          value.FusionMaritimeTarget.fusionMaritimeTargetVisible;
        instance.dataConfig.globale.showPermanentTooltip =
          value.global.showPermanentTooltip;

        const mapContainer = instance.map.getContainer();
        mapContainer
          .querySelectorAll(".leaflet-permanent-tooltip")
          .forEach((tooltip: any) => {
            tooltip.style.opacity = value.global.showPermanentTooltip
              ? "1"
              : "0";
          });
      },
    );
  }

  addCustomToolbarGroup(
    id: string,
    instance?: ILeafletInstance,
    settings?: IAppPluginWidgetSettings,
  ) {
    if (!instance) {
      console.warn("Missing leaflet instance to add custom toolbar.");
      return;
    }

    const showToolbarGroupControl = instance.layerControls.get(
      `showToolbarGroupControl_${id}`,
    );

    if (!showToolbarGroupControl) {
      const showAllAction = (L.Toolbar2 as any).Action.extend({
        options: {
          toolbarIcon: {
            html: '<i class="fa fa-arrows-to-circle fa-lg showAllIcon"></i>',
            className: "leaflet-toolbar-showAll",
            tooltip: this.translator.instant("MAP.SHOW_ALL"),
          },
        },
        initialize: function (map: L.Map) {
          this.map = map;
          this.mapInstance = instance;
          (L.Toolbar2 as any).Action.prototype.initialize.call(this);
        },
        addHooks: function () {
          if (this.mapInstance) {
            const entities: INgsiMapBaseEntity[] = [];
            this.mapInstance.entities.forEach((entity: INgsiMapBaseEntity) => {
              entities.push(entity);
            });

            if (entities.length === 1) {
              const marker = this.mapInstance.markers.get(entities[0].id);
              const zoom = instance.zoom;

              if (marker) {
                const markerLatLng = marker.getLatLng();
                instance.map.setView(markerLatLng, zoom);
              }
            } else {
              const markers: L.Marker[] = [];
              entities.forEach((entity: INgsiMapBaseEntity) => {
                const marker = this.mapInstance.markers.get(`${entity.id}`);
                if (marker) {
                  markers.push(marker);
                }
              });

              const latLngs = markers.map((marker) => marker.getLatLng());
              const markersBounds = L.latLngBounds(latLngs);

              this.mapInstance.map.fitBounds(markersBounds, {
                padding: [50, 50],
              });
            }
          }
        },
      });

      const followTargetAction = (L.Toolbar2 as any).Action.extend({
        options: {
          toolbarIcon: {
            html: '<i class="fa fa-location-crosshairs fa-lg followTargetIcon"></i>',
            className: "leaflet-toolbar-followTarget",
            tooltip: this.translator.instant("MAP.FOLLOW_TARGET"),
          },
        },
        initialize: function (map: L.Map) {
          this.map = map;
          this.mapInstance = instance;
          (L.Toolbar2 as any).Action.prototype.initialize.call(this);
        },
        addHooks: function () {
          if (this.mapInstance) {
            const activeIconElement = this._link.querySelector(
              "i.followTargetIcon.active",
            );
            if (activeIconElement) {
              this.mapInstance.centerOnMarkerActive = false;
              activeIconElement.classList.remove("active");
              return;
            }

            const iconElement = this._link.querySelector("i.followTargetIcon");
            if (iconElement) {
              if (!this.mapInstance.centerOnMarkerActive) {
                this.mapInstance.centerOnMarkerActive = true;
              }
              iconElement.classList.add("active");
            }

            const entities: INgsiMapBaseEntity[] = [];
            this.mapInstance.entities.forEach((entity: INgsiMapBaseEntity) => {
              entities.push(entity);
            });

            if (entities.length === 1) {
              const marker = this.mapInstance.markers.get(entities[0].id);
              if (marker) {
                RouteTracking.followMarker(marker, instance);
              }
            } else {
              const markers: L.Marker[] = [];
              entities.forEach((entity: INgsiMapBaseEntity) => {
                const marker = this.mapInstance.markers.get(`${entity.id}`);
                if (marker) {
                  markers.push(marker);
                }
              });

              const mapBounds = this.mapInstance.map.getBounds();
              const latLngs = markers.map((marker) => marker.getLatLng());
              const markersBounds = L.latLngBounds(latLngs);

              if (!mapBounds.contains(markersBounds)) {
                this.mapInstance.map.fitBounds(markersBounds, {
                  padding: [50, 50],
                });
              }
            }
          }
        },
      });

      const customToolbarGroupControl = new (L.Toolbar2 as any).Control({
        position: "bottomright",
        actions: [showAllAction, followTargetAction],
      });

      instance.layerControls.set(
        `showToolbarGroupControl_${id}`,
        customToolbarGroupControl,
      );
      instance.map.addControl(customToolbarGroupControl);
    } else {
      instance.map.addControl(showToolbarGroupControl);
    }
  }

  addMeasurementControl(id: string, instance?: ILeafletInstance) {
    if (!instance) {
      console.warn("Missing leaflet instance to add measure control button.");
      return;
    }

    const linearMeasurementControl = instance.layerControls.get(
      `linear_measurement_${id}`,
    );

    if (!linearMeasurementControl) {
      const options = {
        position: "bottomright",
        circleMarker: {
          color: "#FF0080",
          radius: 2,
        },
        lineStyle: {
          color: "#FF0080",
          dashArray: "1,6",
        },
        lengthUnit: {
          display: "Nautical Miles",
          decimal: 2,
          factor: 0.539956803,
          label: "Distance:",
        },
        angleUnit: {
          display: "&deg;",
          decimal: 2,
          factor: null,
          label: "Bearing:",
        },
      };

      const rulerControl = new L.Control.Ruler(options);

      const rulerIconButton = document.querySelector(
        ".leaflet-ruler",
      ) as HTMLElement;
      if (rulerIconButton) {
        rulerIconButton.title = this.translator.instant("MAP.MEASURE_DISTANCE");
      }

      instance.layerControls.set(`linear_measurement_${id}`, rulerControl);
      instance.map.addControl(rulerControl);
    } else {
      instance.map.addControl(linearMeasurementControl);
    }
  }

  addMousePositionControl(id: string, instance?: ILeafletInstance) {
    if (!instance) {
      console.warn("Missing leaflet instance to add mouse position.");
      return;
    }

    const mousePositionControl = instance.layerControls.get(
      `mouse_position_${id}`,
    ) as any;

    // Set the initial coordinates to the map's center
    const mapCenter = instance.map.getCenter();
    const simulatedEvent = { latlng: mapCenter };

    if (!mousePositionControl) {
      const options = {
        position: "bottomleft",
        useDMS: true,
        labelTemplateLat: "N {y}",
        labelTemplateLng: "E {x}",
        enableUserInput: false,
        useLatLngOrder: true,
      };
      const coordinatesControl = (L.control as any).coordinates(options as any);
      instance.layerControls.set(`mouse_position_${id}`, coordinatesControl);

      instance.map.addControl(coordinatesControl);
      coordinatesControl._update(simulatedEvent); // Use the internal `_update` method
    } else {
      instance.map.addControl(mousePositionControl);
      mousePositionControl._update(simulatedEvent);
    }
  }

  addVesselOverviewSidePanel(id: string, instance?: ILeafletInstance) {
    if (!instance) {
      console.warn("Missing leaflet instance to add vesselOverviewSidePanel.");
      return;
    }

    const sidePanelElementId = `vesselboard-${id}`;
    const sidePanelControlId = `vesselboard_sidePanel_${id}`;

    const sidePanelElement = document.getElementById(sidePanelElementId);
    if (!sidePanelElement) {
      console.warn(
        "Missing sidePanelElement DOM element to add vesselboardSidePanel.",
      );
      return;
    }

    if (!instance.layerControls.has(sidePanelControlId) && sidePanelElement) {
      const sidePanelOptions = {
        panelPosition: "left",
        hasTabs: false,
        tabsPosition: "top",
        pushControls: false,
        darkMode: false,
        defaultTab: "tab-1",
      } as SidePanelOptions;

      const vesselBoardSidePanelControl = L.control.sidepanel(
        sidePanelElementId,
        sidePanelOptions,
      );
      vesselBoardSidePanelControl.addTo(instance.map);

      instance.layerControls.set(
        sidePanelControlId,
        vesselBoardSidePanelControl,
      );
    }
  }

  viewOnMarkerPosition(
    id: string,
    entity: INgsiMapBaseEntity,
    instance: ILeafletInstance,
  ): void {
    const layerId = `${this.getInstanceId(id)}_${entity.type}`;
    const layer = instance.layers.get(layerId);
    if (layer) {
      const marker = instance.markers.get(entity.id);
      if (marker) {
        RouteTracking.followMarker(marker, instance);
      }
    }
  }

  mapFitBounds(
    id: string,
    type: string,
    initMap: boolean,
    widgetEntities: IMapWidgetFormGroupEntities[],
    instance?: ILeafletInstance,
  ): void {
    if (instance) {
      const mapBounds = instance.map.getBounds();

      const markers: L.Marker[] = [];
      widgetEntities.forEach((entity: IMapWidgetFormGroupEntities) => {
        const marker = instance.markers.get(`urn:ngsi-ld:${entity.id}`);
        if (marker) {
          markers.push(marker);
        }
      });

      if (markers.length > 0) {
        // Collect all focused marker coordinates
        const latLngs = markers.map((marker) => marker.getLatLng());

        // Create bounds from the array of LatLngs
        const markersBounds = L.latLngBounds(latLngs);

        if (initMap) {
          instance.map.fitBounds(markersBounds, {
            padding: [50, 50],
          });
        }

        if (!mapBounds.contains(markersBounds)) {
          instance.map.fitBounds(markersBounds, {
            padding: [50, 50],
          });
        }
      }
    }
  }

  add_locateControl(id: number | string): void {
    const instance = this.getInstance(id);

    if (instance) {
      this.currentLocation = L.control
        .locate({
          flyTo: false,
          showPopup: false,
          keepCurrentZoomLevel: true,
          locateOptions: {
            enableHighAccuracy: true,
          },
          cacheLocation: false,
          clickBehavior: {
            inView: "stop",
            outOfView: "setView",
            inViewNotFollowing: "setView",
          },
          strings: {
            title: "",
          },
        })
        .addTo(instance.map);
    }
  }

  run_localization(): void {
    this.currentLocation.start();
  }

  clearMap(
    skip: boolean,
    id?: number | string,
    instance?: ILeafletInstance,
  ): void {
    // Clear generated hues
    CommonMapHelperService.clearGeneratedHues();

    if (!id || !instance) {
      console.warn(`Missing id or instance to clear leafletInstance`);
      return;
    }

    // Remove the locate control
    if (this.currentLocation) {
      this.currentLocation.stop();
    }

    // Remove markers
    instance.markers.forEach((marker: L.Marker<any>) => marker.remove());
    instance.markers.clear();

    // Remove polylines
    instance.polylines.forEach((polyline: L.Polyline<any>) =>
      polyline.remove(),
    );
    instance.polylines.clear();

    // Remove polygons
    instance.polygons.forEach((polygon: L.Polygon<any>) => polygon.remove());
    instance.polygons.clear();

    // Remove circles
    instance.circles.forEach((circle: L.Circle<any>) => circle.remove());
    instance.circles.clear();

    // Remove trackingRoutes
    instance.trackingRoutes.forEach((route: ITrackingRoute) =>
      route.line.remove(),
    );
    instance.trackingRoutes.clear();

    // Remove layers
    instance.map.eachLayer((layer: L.Layer) => {
      // only tileLayers and toolbar icons will not be removed
      if (!(layer instanceof L.TileLayer)) {
        layer.remove();
      }
    });

    // Clear specific layerControls
    const layerControl = instance.layerControls.get(this.getInstanceId(id));
    if (layerControl && !skip) {
      layerControl.remove();
    }

    instance.layerControls.forEach((layerControl: L.Control) => {
      // Remove the associated DOM element if it's a custom control (e.g., side panel)
      if (!(layerControl as any)?._panel && !skip) {
        layerControl.remove();
      }
    });

    // Clear vesselboard sidePanel
    const vesselboardSidePanelControlId = `vesselboard_sidePanel_${id}`;
    const vesselboardSidePanelControl = instance.layerControls.get(
      vesselboardSidePanelControlId,
    );
    if (vesselboardSidePanelControl?.getContainer()?.parentNode) {
      vesselboardSidePanelControl.remove(); // Remove the control from the map
    }

    // Clear miniMap
    if (instance?.layerControls.has(`minimap_${id}`)) {
      const miniMapControl = instance?.layerControls.get(`minimap_${id}`);
      if (miniMapControl) {
        instance.map.removeControl(miniMapControl);
      }
      instance.layerControls.delete(`minimap_${id}`);
    }

    instance.followTargetIconEl = null;
    instance.rulerIconButtonEl = null;
    instance.zoomInButtonEl = null;
    instance.zoomOutButtonEl = null;

    // Clear layers
    instance.layers.clear();

    // Clear entities and additionalEntities
    instance.entities.clear();
    instance.additionalEntities.clear();

    // Clear scheduledEntityTimeouts
    instance.scheduledEntityTimeouts.clear();

    // Clear timeouts
    instance.timeoutIds.length = 0;
  }

  destroyMap(id: number | string, instance?: ILeafletInstance): void {
    if (instance) {
      instance.map.eachLayer((layer: L.Layer) => {
        layer.off();
        layer.remove();
      });

      // Clear layerControls complete
      instance.layerControls.clear();

      instance.map.off();
      instance.map.removeEventListener("updatingObcNavigationsInstruments");
      instance.map.remove();
      this.mapInstances.delete(this.getInstanceId(id));

      // Clear tileLayers
      instance.tileLayers = {};
    }
  }
}
